import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to maintain timestamp position when the WINDOW resizes
 * 
 * IMPORTANT: This hook ONLY responds to actual window resize events.
 * It does NOT use ResizeObserver because that causes issues with
 * zoom animations, preset changes, and bar dragging operations.
 * 
 * The trade-off: sidebar collapse/expand won't auto-adjust the view,
 * but that's acceptable to avoid scroll position bugs.
 */
export const useGanttResize = (chartRef, dateRange) => {
  // Store the scroll position as a ratio of scrollLeft / timelineWidth
  const scrollRatioRef = useRef(null);
  const lastWindowWidthRef = useRef(window.innerWidth);
  const isResizingRef = useRef(false);
  const resizeTimeoutRef = useRef(null);

  // Calculate scroll ratio from current scroll position
  const getScrollRatio = useCallback(() => {
    if (!chartRef.current) return null;
    
    const scrollContainer = chartRef.current;
    const sidebarWidth = 200;
    const timelineWidth = scrollContainer.scrollWidth - sidebarWidth;
    
    if (timelineWidth <= 0) return null;
    
    return scrollContainer.scrollLeft / timelineWidth;
  }, [chartRef]);

  // Apply scroll ratio to restore position
  const applyScrollRatio = useCallback((ratio) => {
    if (!chartRef.current || ratio === null) return;
    
    const scrollContainer = chartRef.current;
    const sidebarWidth = 200;
    const timelineWidth = scrollContainer.scrollWidth - sidebarWidth;
    
    if (timelineWidth <= 0) return;
    
    const newScrollLeft = ratio * timelineWidth;
    const maxScroll = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
    
    scrollContainer.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
  }, [chartRef]);

  // Only listen to window resize - NOT ResizeObserver
  useEffect(() => {
    const handleWindowResize = () => {
      if (!chartRef.current) return;
      
      const currentWindowWidth = window.innerWidth;
      const widthDelta = Math.abs(currentWindowWidth - lastWindowWidthRef.current);
      
      // Only respond to significant window width changes (> 50px)
      if (widthDelta < 50) {
        lastWindowWidthRef.current = currentWindowWidth;
        return;
      }
      
      // Mark that we're resizing
      if (!isResizingRef.current) {
        isResizingRef.current = true;
        // Capture current scroll ratio at start of resize
        scrollRatioRef.current = getScrollRatio();
      }
      
      // Debounce the actual restoration
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      resizeTimeoutRef.current = setTimeout(() => {
        // Restore scroll position after resize settles
        if (scrollRatioRef.current !== null) {
          applyScrollRatio(scrollRatioRef.current);
        }
        isResizingRef.current = false;
        lastWindowWidthRef.current = window.innerWidth;
      }, 150);
    };
    
    window.addEventListener('resize', handleWindowResize);
    
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [chartRef, getScrollRatio, applyScrollRatio]);

  return {
    getScrollRatio,
    applyScrollRatio,
  };
};

export default useGanttResize;
